// ============================================================
// MultiplayerScreen.tsx — Code-based private rooms (2-4 players)
// "Stairs?! NOOOOOOO!" — Claptrap (but ranked ladders go UP)
// ============================================================

import { useState, useEffect, useRef } from 'react';
import {
  connectMultiplayer,
  disconnectMultiplayer,
  createRoom as wsCreateRoom,
  joinRoom as wsJoinRoom,
  leaveRoom as wsLeaveRoom,
  startGame as wsStartGame,
  isMultiplayerConnected,
} from '../multiplayer/client';
import { getToken, getUser } from '../api/client';
import { loadDictionary } from '../game/dictionary';
import { t, getLang, setLang, LANGUAGES } from '../i18n';
import type { MultiplayerEvent } from '../multiplayer/client';
import type { MultiplayerPeer } from '../game/types';
import type { GameMode } from '../App';

interface Props {
  onGameStart: (seed: number, mode: GameMode, opponentName?: string, roster?: { id: string; name: string }[], opts?: { difficulty?: string; gameMode?: 'normal' | 'miroir' }) => void;
  onBack: () => void;
}

type Phase = 'idle' | 'connecting' | 'lobby';
type LobbyDifficulty = 'egoFriendly' | 'easy' | 'normal' | 'hard';
type LobbyGameMode = 'normal' | 'miroir';

const NAME_KEY = 'wardogs_mp_name';
const DIFFICULTIES: LobbyDifficulty[] = ['egoFriendly', 'easy', 'normal', 'hard'];
const DIFF_LABEL_KEY: Record<LobbyDifficulty, string> = {
  egoFriendly: 'diffEgoFriendly',
  easy: 'diffEasy',
  normal: 'diffNormal',
  hard: 'diffHard',
};

export default function MultiplayerScreen({ onGameStart, onBack }: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [code, setCode] = useState('');
  const [joinInput, setJoinInput] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [peers, setPeers] = useState<MultiplayerPeer[]>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [, forceTick] = useState(0);
  const [hostDifficulty, setHostDifficulty] = useState<LobbyDifficulty>('normal');
  const [hostGameMode, setHostGameMode] = useState<LobbyGameMode>('normal');

  const user = getUser();
  const initialName = user?.username || localStorage.getItem(NAME_KEY) || '';
  const [displayName, setDisplayName] = useState(initialName);
  const matchedRef = useRef(false);
  const currentLang = getLang();

  const handleLangChange = (lc: string) => {
    if (lc === getLang()) return;
    setLang(lc);
    loadDictionary();
    forceTick(n => n + 1);
  };

  // Disconnect when leaving without a started match.
  useEffect(() => {
    return () => {
      if (!matchedRef.current) disconnectMultiplayer();
    };
  }, []);

  const errMsgFromServer = (m: string): string => {
    switch (m) {
      case 'invalid_code': return t('invalidCode');
      case 'room_not_found': return t('roomNotFound');
      case 'room_full': return t('roomFull');
      case 'room_already_started': return t('roomStarted');
      case 'need_two_players': return t('needTwoPlayers');
      default: return m;
    }
  };

  const handleEvent = (event: MultiplayerEvent) => {
    switch (event.type) {
      case 'connected':
        // Wait — we send create/join after ensuring connection in the click handlers.
        break;
      case 'roomCreated':
        setCode(event.code);
        setPlayerId(event.playerId);
        setIsHost(true);
        setPeers(event.players);
        setPhase('lobby');
        setErrorMsg('');
        break;
      case 'roomJoined':
        setCode(event.code);
        setPlayerId(event.playerId);
        setIsHost(event.isHost);
        setPeers(event.players);
        setPhase('lobby');
        setErrorMsg('');
        break;
      case 'peerJoined':
        setPeers(event.players);
        break;
      case 'peerLeft':
        setPeers(event.players);
        // If we're now the host (host left), update flag.
        if (event.hostId === playerId) setIsHost(true);
        break;
      case 'gameStarted': {
        matchedRef.current = true;
        const others = event.players
          .filter(p => p.id !== playerId)
          .map(p => ({ id: p.id, name: p.name }));
        const firstName = others[0]?.name ?? '';
        onGameStart(event.seed, 'online', firstName, others, {
          difficulty: event.difficulty,
          gameMode: event.gameMode,
        });
        break;
      }
      case 'roomError':
        setErrorMsg(errMsgFromServer(event.message));
        break;
      case 'error':
        setErrorMsg(event.message);
        setPhase('idle');
        break;
      case 'disconnected':
        if (matchedRef.current) return;
        setPhase('idle');
        setPeers([]);
        setCode('');
        break;
    }
  };

  const ensureConnected = (after: () => void) => {
    if (isMultiplayerConnected()) {
      after();
      return;
    }
    setPhase('connecting');
    setErrorMsg('');
    let fired = false;
    const name = displayName.trim().slice(0, 20);
    if (name) localStorage.setItem(NAME_KEY, name);
    connectMultiplayer((event) => {
      if (event.type === 'connected' && !fired) {
        fired = true;
        after();
      } else {
        handleEvent(event);
      }
    }, getToken() || undefined, name || undefined);
  };

  const handleCreate = () => {
    ensureConnected(() => wsCreateRoom());
  };

  const handleJoin = () => {
    const c = joinInput.trim().toUpperCase();
    if (c.length < 3) {
      setErrorMsg(t('invalidCode'));
      return;
    }
    ensureConnected(() => wsJoinRoom(c));
  };

  const handleStart = () => {
    if (peers.length < 2) {
      setErrorMsg(t('needTwoPlayers'));
      return;
    }
    wsStartGame({ difficulty: hostDifficulty, gameMode: hostGameMode });
  };

  const handleLeave = () => {
    wsLeaveRoom();
    disconnectMultiplayer();
    setPhase('idle');
    setPeers([]);
    setCode('');
    setIsHost(false);
    setErrorMsg('');
  };

  const handleBack = () => {
    if (phase === 'lobby') {
      try { wsLeaveRoom(); } catch { /* ignore */ }
    }
    disconnectMultiplayer();
    onBack();
  };

  return (
    <div className="screen active" id="multiplayer-screen">
      <div className="multiplayer-content">
        <h1 className="menu-title" style={{ fontSize: '2.5rem' }}>{t('multiplayer')}</h1>

        {phase === 'idle' && (
          <>
            <div className="lang-picker mp-lang-picker">
              {Object.values(LANGUAGES).map(l => (
                <button
                  key={l.code}
                  className={`lang-btn ${currentLang === l.code ? 'active' : ''}`}
                  onClick={() => handleLangChange(l.code)}
                >
                  {l.name}
                </button>
              ))}
            </div>

            <p className="menu-subtitle">2-4 — {t('subtitle')}</p>

            {!user && (
              <div className="mp-name-row">
                <label className="score-label" htmlFor="mp-name-input">{t('yourName')}</label>
                <input
                  id="mp-name-input"
                  type="text"
                  className="mp-name-input"
                  value={displayName}
                  maxLength={20}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Joueur"
                  autoComplete="off"
                />
              </div>
            )}

            <button className="btn-primary" onClick={handleCreate}>{t('createRoom')}</button>

            <div className="mp-join-row">
              <input
                type="text"
                className="mp-code-input"
                value={joinInput}
                onChange={e => setJoinInput(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
                placeholder={t('enterCode')}
                maxLength={4}
                autoComplete="off"
              />
              <button className="btn-primary" onClick={handleJoin} disabled={joinInput.length < 3}>
                {t('joinRoom')}
              </button>
            </div>

            {errorMsg && <p className="auth-error">{errorMsg}</p>}
            <button className="btn-secondary" onClick={handleBack}>{t('back')}</button>
          </>
        )}

        {phase === 'connecting' && (
          <>
            <div className="matchmaking-spinner">
              <div className="spinner" />
              <p>{t('waiting')}</p>
            </div>
            <button className="btn-secondary" onClick={handleBack}>{t('back')}</button>
          </>
        )}

        {phase === 'lobby' && (
          <>
            <div className="mp-code-display">
              <span className="score-label">{t('shareCode')}</span>
              <span className="mp-code-value">{code}</span>
            </div>

            <div className="mp-roster">
              <div className="mp-roster-header">
                <span>{t('playersCount')}</span>
                <span>{peers.length}/4</span>
              </div>
              <ul className="mp-roster-list">
                {peers.map(p => (
                  <li key={p.id} className={p.id === playerId ? 'me' : ''}>
                    <span className="mp-peer-name">{p.name}</span>
                    {p.isHost && <span className="mp-peer-tag">{t('host')}</span>}
                    {p.id === playerId && <span className="mp-peer-tag">{t('you')}</span>}
                  </li>
                ))}
              </ul>
            </div>

            {errorMsg && <p className="auth-error">{errorMsg}</p>}

            {isHost && (
              <>
                <div className="mp-config">
                  <span className="score-label">{t('difficulty')}</span>
                  <div className="mp-config-row">
                    {DIFFICULTIES.map(d => (
                      <button
                        key={d}
                        className={`mp-config-btn ${hostDifficulty === d ? 'active' : ''}`}
                        onClick={() => setHostDifficulty(d)}
                      >
                        {t(DIFF_LABEL_KEY[d])}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mp-config">
                  <span className="score-label">{t('gameMode')}</span>
                  <div className="mp-config-row">
                    <button
                      className={`mp-config-btn ${hostGameMode === 'normal' ? 'active' : ''}`}
                      onClick={() => setHostGameMode('normal')}
                    >
                      {t('modeStandard')}
                    </button>
                    <button
                      className={`mp-config-btn ${hostGameMode === 'miroir' ? 'active' : ''}`}
                      onClick={() => setHostGameMode('miroir')}
                    >
                      {t('modeMiroir')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {isHost ? (
              <button
                className="btn-primary"
                onClick={handleStart}
                disabled={peers.length < 2}
                title={peers.length < 2 ? t('needTwoPlayers') : ''}
              >
                {t('startMatch')}
              </button>
            ) : (
              <p className="menu-subtitle">{t('waitingPlayers')}</p>
            )}

            <button className="btn-secondary" onClick={handleLeave}>{t('leaveRoom')}</button>
          </>
        )}
      </div>
    </div>
  );
}
